import React from 'react';
import { Link } from 'react-router-dom';
import { ResourceType } from '@cotera/api';
import { Icon, Tooltip } from '@cotera/client/app/components/ui';
import { Skeleton } from '@cotera/client/v0/components/ui/skeleton';
import { useWhoami } from '@cotera/client/app/stores/org';
import { cn } from '@cotera/client/v0/lib/utils';
import {
  RESOURCE_LIST_CELL_TEXT,
  RESOURCE_LIST_CELL_TEXT_SIZE,
} from '../resources/list/resource-list-types';
import { formatLastRun } from '../workflow/use-workflow-summaries';
import {
  isActiveRunStatus,
  runStatusLabel,
} from '../workflow/workflow-run-status';
import type { ItemAction } from '../resources/list/resource-list-view';
import { RowActionsMenu } from '../resources/list/row-actions-menu';
import { FavoriteRowMarker } from '../resources/favorites';
import { resourceTypeIconRingClassName } from '../resources/type/resource-type-colors';

/** Row of a workflow bound to a trigger, passed to the per-row actions builder. */
export type WorkflowRowRef = { workflowId: string; name: string };

/**
 * Shared row geometry for the Automations list. Trigger rows, the workflow rows
 * nested under them, and the list's column header are all hand-rolled (not
 * rendered through `ResourceListView`), so these constants are the one source of
 * truth that keeps all three lined up — change a width here and the header
 * follows, because it reads the same constants.
 */

/** Caret gutter at the far left of a group header row. */
export const LIST_ROW_CHEVRON_GUTTER = 'w-7 shrink-0';
/**
 * Leading gutter for a row nested under a group: 28px clearing the parent's caret
 * column, then a 20px indent whose left edge carries the tree line — so the line
 * lands *at* the caret's position rather than floating mid-gutter, and adjacent
 * rows join it into one continuous edge down the group.
 *
 * Children used to reuse `LIST_ROW_CHEVRON_GUTTER`, deliberately, so that a
 * child's icon landed on the same left edge as its parent's. That is precisely
 * what made an expanded group unreadable: nothing but the caret distinguished a
 * trigger from the workflows under it. Only the flexible name column absorbs the
 * 48px, so the status and timing columns stay aligned across both row types.
 */
export const NestedRowGutter: React.FC = () => (
  <>
    <span className="w-7 shrink-0" aria-hidden="true" />
    <span
      className="w-5 shrink-0 self-stretch border-l border-border/60"
      aria-hidden="true"
    />
  </>
);
/**
 * Where a row's name starts, measured from *after* the gutter span: icon bubble
 * (32px) + the icon-to-name gap (16px), or 76px from the row's left edge once the
 * 28px gutter is counted. Anything that has to line up with the names — the
 * column header, section labels, the nested empty/loading lines, the add-row
 * affordances — hangs off this.
 */
export const LIST_ROW_NAME_INSET_AFTER_GUTTER = 'pl-12';

/**
 * Health column. Held back to `lg` — below that the two timing columns already
 * eat the row, and the states it shows have inline fallbacks on the name (see
 * the `lg:hidden` chips).
 */
export const LIST_ROW_STATUS_CELL =
  'hidden w-[140px] shrink-0 px-3 text-left lg:block';
/** Timing columns (last run / next run). */
export const LIST_ROW_CELL =
  'hidden w-[170px] shrink-0 px-3 text-right md:block';
/**
 * Trailing slot for the row's `…` menu. Permanently reserved but empty until the
 * row is hovered: the menu used to swap places with the leading icon, which meant
 * the icon vanished whenever the pointer crossed a row on its way to clicking it.
 * A slot of its own costs 32px and lets the icon always be the icon.
 */
export const LIST_ROW_ACTIONS_CELL = 'w-8 shrink-0';
/** Mirrors of the two widths above, for `Column.width` on the loading skeleton. */
export const LIST_ROW_STATUS_WIDTH = '140px';
export const LIST_ROW_TIME_WIDTH = '170px';

/**
 * How healthy a row is, in the four states worth telling apart at a glance:
 * doing its job, working right now, broken, and deliberately not going to run.
 * Never "unknown" — a status this file doesn't recognize keeps its own name (see
 * {@link runStatusPresentation}) rather than being flattened into one of these.
 */
export type ListRowStatusTone = 'ok' | 'running' | 'failed' | 'idle';

const STATUS_TONE_DOT: Record<ListRowStatusTone, string> = {
  ok: 'bg-success',
  // Live work earns the only motion on the row.
  running: 'bg-amber-500 animate-pulse-scale',
  failed: 'bg-red-500',
  idle: 'bg-muted-foreground/30',
};

/**
 * Dot + word for the Status column. A failure is the one state that gets colored
 * text as well as a colored dot — everything else stays quiet so a broken
 * automation is the thing your eye lands on when scanning the column.
 */
/**
 * Just the dot's colour for a run status, for callers that render their own
 * label (the Library's list rows). Derived from {@link runStatusPresentation}
 * rather than its own switch: an earlier standalone copy of this never handled
 * `incomplete` — the status an in-progress run actually carries — so a running
 * workflow showed a grey dot.
 */
export function runStatusDotClass(status: string | null): string {
  return STATUS_TONE_DOT[runStatusPresentation(status).tone];
}

export const ListRowStatus: React.FC<{
  tone: ListRowStatusTone;
  label: string;
}> = ({ tone, label }) => (
  <span
    className={cn(
      'inline-flex items-center gap-2 whitespace-nowrap',
      RESOURCE_LIST_CELL_TEXT_SIZE,
      tone === 'failed' ? 'font-medium text-red-600' : 'text-muted-foreground'
    )}
  >
    <span
      className={cn('h-1.5 w-1.5 shrink-0 rounded-full', STATUS_TONE_DOT[tone])}
      aria-hidden="true"
    />
    {label}
  </span>
);

/**
 * A trigger's status, drawn as one thin bar per bound workflow — the same little
 * bar strip the dataset grid uses for its automations column, so a trigger and a
 * dataset report their workflows' health the same way. Each bar takes its colour
 * from that workflow's most-recent run through the trigger (green ok, red failed,
 * amber running, muted idle/never), and hovering it shows how many of that
 * workflow's runs failed in the 24h before that run.
 *
 * This lives on the trigger (group header) row and is the *only* status the
 * Automations list shows: the workflow rows nested under a trigger no longer
 * carry their own run-status word, so a broken automation reads as one red bar in
 * one place rather than the same fact repeated as `Failing` on the parent and
 * `Failed` on the child.
 */
export const TriggerStatusBars: React.FC<{
  workflows: {
    workflowId: string;
    name: string;
    lastRunStatus: string | null;
    failedRuns: number;
    totalRuns: number;
  }[];
  /** Cap on bars drawn before collapsing the rest into a `+N`. */
  maxBars?: number;
}> = ({ workflows, maxBars = 24 }) => {
  if (workflows.length === 0) {
    // A trigger with nothing attached has no health of its own — an em-dash, the
    // same empty-cell convention the timing columns use.
    return (
      <span
        className={cn(RESOURCE_LIST_CELL_TEXT_SIZE, 'text-muted-foreground/50')}
      >
        —
      </span>
    );
  }
  const shown = workflows.slice(0, maxBars);
  const overflow = workflows.length - shown.length;
  return (
    // The bars need their hover to survive the trigger row's expand hit-area.
    // That hit-area is the accordion trigger's `before:inset-0` pseudo — being
    // absolutely positioned, it paints *above* this static Status cell (which is
    // itself `pointer-events-none` so clicks fall through to it). `relative z-[1]`
    // lifts the bars back above that pseudo, and `pointer-events-auto` lets them
    // take the hover, so the tooltip fires — the same trick the row's actions
    // menu uses. `z-[1]` stays under the sticky list header (`z-10`).
    <span className="pointer-events-auto relative z-[1] flex items-center gap-px">
      {shown.map((w) => {
        const statusLabel = runStatusPresentation(w.lastRunStatus).label;
        const tooltip =
          w.lastRunStatus === null ? (
            <span>
              <span className="font-medium">{w.name}</span> — never run
            </span>
          ) : (
            <span>
              <span className="font-medium">{w.name}</span> — last run{' '}
              {statusLabel}
              {w.totalRuns > 0 ? (
                <>
                  {' · '}
                  {Math.round((w.failedRuns / w.totalRuns) * 100)}% failed over
                  24h ({w.failedRuns}/{w.totalRuns} runs)
                </>
              ) : null}
            </span>
          );
        return (
          <Tooltip
            key={w.workflowId}
            side="center"
            placement="top"
            asChild
            openDelay={120}
            closeDelay={80}
            tooltipContent={tooltip}
          >
            {/* A taller/padded wrapper is the hover target, so the 4px bar isn't
                a pixel-hunt; the bar itself keeps its `h-3 w-1` look. */}
            <span className="flex h-4 items-center px-px">
              <span
                className={cn(
                  'h-3 w-1 rounded-[1px]',
                  runStatusDotClass(w.lastRunStatus)
                )}
              />
            </span>
          </Tooltip>
        );
      })}
      {overflow > 0 ? (
        <span
          className={cn(
            RESOURCE_LIST_CELL_TEXT_SIZE,
            'ml-1 text-muted-foreground'
          )}
        >
          +{overflow}
        </span>
      ) : null}
    </span>
  );
};

/**
 * Shimmering stand-in for {@link TriggerStatusBars}, shaped like a few bars, shown
 * in a trigger's Status cell while the batched summaries are still loading. The
 * rollups load independently of the page, so a row paints immediately with just
 * this cell pending rather than the whole list waiting on run history.
 */
export const TriggerStatusBarsSkeleton: React.FC = () => (
  <span className="flex items-center gap-px" aria-hidden="true">
    {[0, 1, 2].map((i) => (
      <Skeleton key={i} className="h-3 w-1 rounded-[1px]" />
    ))}
  </span>
);

/**
 * Run status → a dot + word. Shared by the workflow rows here and the schedule
 * view's agenda, so a run reads the same wherever this page shows it.
 *
 * The label comes from `runStatusLabel`, the app's one run-status vocabulary, and
 * only the *tone* is decided here. An earlier local switch duplicated that
 * vocabulary and drifted from it: it never handled `incomplete`, which is the
 * status an in-progress run actually carries (`running` is step-level only — runs
 * roll it up), so a workflow running right now rendered as a grey "Idle", and
 * `cancelled`/`stopped` did the same. Deriving the label instead of restating it
 * means a status added later shows its own name rather than falling into a
 * catch-all.
 */
export function runStatusPresentation(status: string | null): {
  tone: ListRowStatusTone;
  label: string;
} {
  if (status === null) {
    return { tone: 'idle', label: 'Idle' };
  }
  const label = runStatusLabel(status);
  if (status === 'failed') {
    return { tone: 'failed', label };
  }
  if (status === 'completed') {
    return { tone: 'ok', label };
  }
  if (isActiveRunStatus(status)) {
    return { tone: 'running', label };
  }
  // Terminal but not a success: cancelled, stopped, and anything added later.
  return { tone: 'idle', label };
}

/**
 * Workflow row on the Automations list (nested under a trigger, or standalone).
 * Rendered flat — no card border — so it reads as another line of the same table
 * as the trigger rows above it, matching the Library list. Row actions live on
 * the leading icon (hover swaps it to an `…` menu) to match the Library, so the
 * icon sits outside the navigation `<Link>` (a menu button can't be nested inside
 * an anchor).
 */
export const WorkflowRow: React.FC<{
  workflowId: string;
  name: string;
  lastRunAt: Date | null;
  bindingOff?: boolean;
  actions?: ItemAction[];
  /** Fired when the leading-icon menu opens (target the row's selection). */
  onOpenActionMenu?: () => void;
}> = ({
  workflowId,
  name,
  lastRunAt,
  bindingOff = false,
  actions,
  onOpenActionMenu,
}) => {
  const org = useWhoami((s) => s.org);

  // Color-coded bubble (the Library's row-icon treatment) so a workflow row is
  // recognizable as a workflow at a glance, next to the ochre trigger bubbles.
  const leadingIcon = (
    <span
      className={cn(
        'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-sm',
        resourceTypeIconRingClassName(ResourceType.Workflow)
      )}
    >
      <Icon icon="workflow" className="h-4 w-4" />
    </span>
  );

  return (
    <div
      className={cn(
        'group/row relative isolate flex w-full min-w-0 items-center',
        /* The row rule, as an inset `after:` pseudo rather than a `border-b`, so
           it starts at the tree line (`left-7` — the 28px `NestedRowGutter`
           draws at) instead of crossing it and leaving a T-junction. */
        'after:absolute after:bottom-0 after:left-7 after:right-0 after:h-px after:bg-border/40 last:after:hidden',
        /* The hover tint, also inset to the tree line, and rounded to the header
           `+` button's `rounded-sm`. It has to be a pseudo rather than a
           background on the row: a background can only be inset by masking the
           gutter, and a square mask can't meet a rounded tint. `isolate` plus
           `-z-10` is what keeps it behind the row's own content — a positioned
           pseudo otherwise paints above in-flow text. */
        'before:absolute before:inset-y-0 before:left-7 before:right-0 before:-z-10 before:rounded-sm before:transition-colors before:content-[""] hover:before:bg-secondary/60'
      )}
    >
      <NestedRowGutter />
      {leadingIcon}
      <Link
        to={`/${org.slug}/workflows/${workflowId}`}
        // `before:inset-0` stretches the link over the whole row, so the run
        // cells on the right navigate too.
        // `py-3.5` sets the row height, and so the gap between one child and the
        // next. The tree line and the gutter mask both `self-stretch`, so they
        // follow it — which is why the breathing room has to come from padding
        // rather than a margin between rows, since a margin would break the line
        // into dashes.
        className="flex min-w-0 flex-1 items-center gap-2.5 py-3.5 pl-4 pr-3 before:absolute before:inset-0 before:content-['']"
        onClick={(e) => {
          if (e.shiftKey) {
            // Let the parent list handle range selection; don't navigate.
            e.preventDefault();
            return;
          }
          e.stopPropagation();
        }}
      >
        {/* The star trails the name inside this wrapper rather than beside it,
            for the same reason as the trigger rows above: `flex-1` lives here,
            so a sibling would be pushed to the far edge of the column. */}
        <span className="flex min-w-0 flex-1 items-center gap-1.5">
          <span className="min-w-0 truncate text-sm text-slate-700">
            {name}
          </span>
          <FavoriteRowMarker type={ResourceType.Workflow} id={workflowId} />
        </span>
        {/* Run status now lives once, as a bar on the trigger row above (see
            `TriggerStatusBars`), so a nested workflow no longer restates it.
            Paused is the exception — it changes what the row will *do* next, not
            what its last run did — so it stays here as an inline badge beside the
            name, where there's no Status column below `lg` regardless. */}
        {bindingOff ? (
          <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            paused
          </span>
        ) : null}
      </Link>
      {/* Status belongs to the trigger row's bars now, not the workflow — held as
          an empty cell so the Last-run column stays aligned with the header. */}
      <div className={LIST_ROW_STATUS_CELL} aria-hidden="true" />
      {/* Last run is the workflow's own: a run is a thing a workflow does. */}
      <div className={cn(LIST_ROW_CELL, 'pointer-events-none')}>
        <span className={cn(RESOURCE_LIST_CELL_TEXT, 'whitespace-nowrap')}>
          {lastRunAt !== null ? formatLastRun(lastRunAt) : 'Never run'}
        </span>
      </div>
      {/* Next run belongs to the trigger — a workflow has no schedule of its
          own — so the cell is held empty to keep the columns aligned. */}
      <div className={LIST_ROW_CELL} aria-hidden="true" />
      {/* `z-[1]` (not `z-10`) — it only has to beat the link's z-auto
          `::before`, and must stay under the sticky list header (`z-10`). */}
      <div
        className={cn(
          LIST_ROW_ACTIONS_CELL,
          'relative z-[1] flex items-center justify-center opacity-0 transition-opacity focus-within:opacity-100 group-hover/row:opacity-100 has-[button[data-state=open]]:opacity-100'
        )}
      >
        <RowActionsMenu
          actions={actions ?? []}
          onOpen={onOpenActionMenu}
          label={`Actions for ${name}`}
        />
      </div>
    </div>
  );
};
